import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { 
  Trophy, CheckCircle2, Target, TrendingUp, ChevronDown, 
  Zap, Users, X, Calendar, Clock, ArrowRight, Filter,
  Flame, Award, BarChart3, History as HistoryIcon, Star,
  CircleDot, Ban, Pause, RotateCcw
} from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { format, formatDistanceToNow, isThisMonth, isThisYear, parseISO } from 'date-fns';
import { cn } from '../lib/utils';
import { getPandaLevel, PANDA_LEVELS, MONK_IMAGE_URL } from '../components/MonkLevel';

// ============================================
// PERFORMANCE RING - VISUAL SUCCESS RATE
// ============================================
function PerformanceRing({ completed, failed, size = 120, isDark }) {
  const total = completed + failed;
  const successRate = total > 0 ? (completed / total) * 100 : 0;
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (successRate / 100) * circumference;
  
  const getColor = () => {
    if (successRate >= 90) return { stroke: '#10b981', text: isDark ? 'text-emerald-400' : 'text-emerald-600', bg: 'bg-emerald-500/10' };
    if (successRate >= 70) return { stroke: '#f59e0b', text: isDark ? 'text-coral' : 'text-coral', bg: 'bg-[#FDF0EC]0/10' };
    if (successRate >= 50) return { stroke: '#f97316', text: isDark ? 'text-orange-400' : 'text-coral', bg: 'bg-coral/10' };
    return { stroke: '#ef4444', text: isDark ? 'text-red-400' : 'text-red-600', bg: 'bg-red-500/10' };
  };
  
  const colors = getColor();
  
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r="45"
          fill="none"
          stroke={isDark ? "#27272a" : "#e2e8f0"}
          strokeWidth="8"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r="45"
          fill="none"
          stroke={colors.stroke}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn("text-3xl font-bold", colors.text)}>{successRate.toFixed(0)}%</span>
        <span className={cn("text-xs", isDark ? "text-zinc-500" : "text-[#5F5F5F]")}>Success Rate</span>
      </div>
    </div>
  );
}

// ============================================
// STAT CARD - COMPACT METRIC DISPLAY
// ============================================
function StatCard({ icon: Icon, value, label, colorClass, subtext, isDark }) {
  return (
    <div className={cn(
      "flex items-center gap-3 p-4 rounded-xl border transition-colors",
      isDark 
        ? "bg-zinc-900/50 border-white/5 hover:bg-zinc-800/50" 
        : "bg-white shadow-sm border-[#EEEAE0]/50 hover:shadow-md"
    )}>
      <div className={cn("p-2.5 rounded-lg", colorClass)}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-[#1A1A1A]")}>{value}</p>
        <p className={cn("text-xs", isDark ? "text-zinc-500" : "text-[#5F5F5F]")}>{label}</p>
        {subtext && <p className={cn("text-[10px]", isDark ? "text-zinc-600" : "text-[#9E8778]")}>{subtext}</p>}
      </div>
    </div>
  );
}

// ============================================
// GOAL HISTORY CARD
// ============================================
function GoalHistoryCard({ goal, type = 'individual', isDark }) {
  const navigate = useNavigate();
  
  const statusConfig = {
    COMPLETED: { 
      icon: Trophy, 
      color: isDark ? 'text-emerald-400' : 'text-emerald-600', 
      bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50', 
      border: isDark ? 'border-emerald-500/20' : 'border-[#EEEAE0]',
      label: 'Completed',
      dotColor: 'bg-emerald-500'
    },
    FAILED: { 
      icon: X, 
      color: isDark ? 'text-red-400' : 'text-red-600', 
      bg: isDark ? 'bg-red-500/10' : 'bg-red-50', 
      border: isDark ? 'border-red-500/20' : 'border-red-200',
      label: 'Failed',
      dotColor: 'bg-red-500'
    },
    STOPPED: { 
      icon: Ban, 
      color: isDark ? 'text-zinc-400' : 'text-[#5F5F5F]', 
      bg: isDark ? 'bg-zinc-500/10' : 'bg-slate-50', 
      border: isDark ? 'border-zinc-500/20' : 'border-slate-200',
      label: 'Stopped',
      dotColor: isDark ? 'bg-zinc-500' : 'bg-slate-400'
    },
    PAUSED: { 
      icon: Pause, 
      color: 'text-coral', 
      bg: 'bg-[#FDF0EC]0/10', 
      border: 'border-coral/20',
      label: 'Paused',
      dotColor: 'bg-[#FDF0EC]0'
    },
    ACTIVE: { 
      icon: Target, 
      color: 'text-blue-400', 
      bg: 'bg-blue-500/10', 
      border: 'border-blue-500/20',
      label: 'Active',
      dotColor: 'bg-blue-500'
    },
    EXPIRED: { 
      icon: Clock, 
      color: isDark ? 'text-orange-400' : 'text-coral', 
      bg: isDark ? 'bg-coral/10' : 'bg-orange-50', 
      border: isDark ? 'border-coral/20' : 'border-orange-200',
      label: 'Expired',
      dotColor: 'bg-coral'
    },
    CANCELLED: { 
      icon: Ban, 
      color: isDark ? 'text-zinc-400' : 'text-[#5F5F5F]', 
      bg: isDark ? 'bg-zinc-500/10' : 'bg-slate-50', 
      border: isDark ? 'border-zinc-500/20' : 'border-slate-200',
      label: 'Cancelled',
      dotColor: isDark ? 'bg-zinc-500' : 'bg-slate-400'
    }
  };
  
  const config = statusConfig[goal.status] || statusConfig.STOPPED;
  const Icon = config.icon;
  
  // Calculate progress
  const progress = type === 'shared' 
    ? ((goal.user_progress || 0) / goal.target_value) * 100
    : goal.daily_logs 
      ? (goal.daily_logs.filter(l => l.value > 0).length / goal.target_count) * 100
      : 0;
  
  const endDate = goal.end_date || goal.deadline;
  const dateStr = endDate ? format(new Date(endDate), 'MMM d, yyyy') : 'N/A';
  const timeAgo = endDate ? formatDistanceToNow(new Date(endDate), { addSuffix: true }) : '';
  
  const handleClick = () => {
    if (type === 'shared') {
      navigate(`/shared-goals/${goal.id}`);
    }
  };
  
  return (
    <div 
      className={cn(
        "group relative p-4 rounded-xl border transition-all duration-200",
        isDark ? "bg-zinc-900/40 hover:bg-zinc-800/50" : "bg-white shadow-sm hover:shadow-md",
        config.border,
        type === 'shared' && "cursor-pointer"
      )}
      onClick={handleClick}
      data-testid={`history-goal-${goal.id}`}
    >
      {/* Status indicator line */}
      <div className={cn("absolute left-0 top-4 bottom-4 w-1 rounded-full", config.dotColor)} />
      
      <div className="pl-3">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0 pr-3">
            <h4 className={cn("font-medium text-sm truncate mb-1", isDark ? "text-white" : "text-[#1A1A1A]")}>{goal.title}</h4>
            <div className={cn("flex items-center gap-2 text-xs", isDark ? "text-zinc-500" : "text-[#5F5F5F]")}>
              <Calendar className="w-3 h-3" />
              <span>{dateStr}</span>
              <span className={isDark ? "text-zinc-600" : "text-[#9E8778]"}>•</span>
              <span className={isDark ? "text-zinc-600" : "text-[#9E8778]"}>{timeAgo}</span>
            </div>
          </div>
          
          {/* Status Badge */}
          <div className={cn(
            "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium",
            config.bg, config.color
          )}>
            <Icon className="w-3.5 h-3.5" />
            <span>{config.label}</span>
          </div>
        </div>
        
        {/* Progress */}
        <div className="mb-3">
          <div className="flex justify-between text-xs mb-1.5">
            <span className={isDark ? "text-zinc-500" : "text-[#5F5F5F]"}>Progress</span>
            <span className={cn("font-medium", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>
              {type === 'shared' 
                ? `${goal.user_progress || 0}/${goal.target_value} ${goal.target_unit}`
                : `${Math.round(progress)}%`
              }
            </span>
          </div>
          <div className={cn("h-1.5 rounded-full overflow-hidden", isDark ? "bg-zinc-800" : "bg-slate-200")}>
            <div 
              className={cn(
                "h-full rounded-full transition-all duration-500",
                goal.status === 'COMPLETED' ? 'bg-emerald-500' : 
                goal.status === 'FAILED' ? 'bg-red-500' : 
                'bg-[#FDF0EC]0'
              )}
              style={{ width: `${Math.min(100, progress)}%` }}
            />
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className={cn("flex items-center gap-3 text-xs", isDark ? "text-zinc-500" : "text-[#5F5F5F]")}>
            {type === 'shared' ? (
              <>
                <div className="flex items-center gap-1">
                  <Users className="w-3 h-3 text-violet-400" />
                  <span>{goal.participant_count} members</span>
                </div>
              </>
            ) : (
              <>
                {goal.has_pledge && (
                  <div className={cn("flex items-center gap-1", isDark ? "text-emerald-400" : "text-emerald-500")}>
                    <CheckCircle2 className="w-3 h-3" />
                    <span>Tracked</span>
                  </div>
                )}
                {goal.penalty_amount > 0 && (
                  <span className={isDark ? "text-zinc-600" : "text-[#5F5F5F]"}>${goal.penalty_amount} stake</span>
                )}
              </>
            )}
          </div>
          
          {type === 'shared' && (
            <ArrowRight className={cn("w-4 h-4 transition-colors", isDark ? "text-zinc-600 group-hover:text-zinc-400" : "text-[#9E8778] group-hover:text-[#5F5F5F]")} />
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// TIMELINE SECTION HEADER
// ============================================
function TimelineHeader({ title, count, icon: Icon, colorClass, isDark }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className={cn("p-2 rounded-lg", colorClass)}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <h3 className={cn("text-base font-semibold", isDark ? "text-white" : "text-[#1A1A1A]")}>{title}</h3>
        <p className={cn("text-xs", isDark ? "text-zinc-500" : "text-[#5F5F5F]")}>{count} goal{count !== 1 ? 's' : ''}</p>
      </div>
    </div>
  );
}

// ============================================
// MAIN HISTORY PAGE
// ============================================
export default function HistoryPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [stats, setStats] = useState(null);
  const [goals, setGoals] = useState([]);
  const [sharedGoals, setSharedGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, completed, failed, shared
  const [showMonkRanks, setShowMonkRanks] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, goalsRes, sharedRes] = await Promise.all([
        api.get('/api/stats'),
        api.get('/api/goals'),
        api.get('/api/shared-goals').catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setGoals(goalsRes.data);
      setSharedGoals(sharedRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Computed values
  const { completedGoals, failedGoals, activeGoals, stoppedGoals, allPastGoals } = useMemo(() => {
    const completed = goals.filter(g => g.status === 'COMPLETED');
    const failed = goals.filter(g => g.status === 'FAILED');
    const active = goals.filter(g => g.status === 'ACTIVE');
    const stopped = goals.filter(g => g.status === 'STOPPED' || g.status === 'PAUSED');
    const past = goals.filter(g => g.status !== 'ACTIVE' && g.status !== 'DRAFT');
    
    return { 
      completedGoals: completed, 
      failedGoals: failed, 
      activeGoals: active,
      stoppedGoals: stopped,
      allPastGoals: past.sort((a, b) => new Date(b.end_date || b.created_at) - new Date(a.end_date || a.created_at))
    };
  }, [goals]);

  const filteredGoals = useMemo(() => {
    switch (filter) {
      case 'completed':
        return allPastGoals.filter(g => g.status === 'COMPLETED');
      case 'failed':
        return allPastGoals.filter(g => g.status === 'FAILED');
      case 'shared':
        return []; // Handled separately
      default:
        return allPastGoals;
    }
  }, [allPastGoals, filter]);

  const monkInfo = getPandaLevel(stats?.level || 1);
  const xpInLevel = (stats?.xp || 0) % 500;
  const nextRankInfo = PANDA_LEVELS.find(p => p.min > (stats?.level || 1));

  if (loading) {
    return (
      <div className={cn(
        "min-h-screen transition-colors duration-300",
        isDark ? "bg-[#050505]" : "bg-[#FAF9F5]"
      )}>
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className={cn(
            "w-10 h-10 rounded-full border-4 animate-spin",
            isDark ? "border-zinc-800 border-t-amber-500" : "border-[#EEEAE0] border-t-amber-500"
          )} />
        </div>
      </div>
    );
  }

  const totalCompleted = completedGoals.length + sharedGoals.filter(g => g.status === 'COMPLETED' || (g.user_progress >= g.target_value)).length;
  const totalFailed = failedGoals.length + sharedGoals.filter(g => g.status === 'FAILED' || g.status === 'EXPIRED').length;

  return (
    <div className={cn(
      "min-h-screen transition-colors duration-300",
      isDark ? "bg-[#050505]" : "bg-[#FAF9F5]"
    )} data-testid="history-page">
      <Navbar />
      
      <div className="max-w-6xl mx-auto p-4 md:p-6 lg:p-8">
        {/* Clean header */}
        <div className="mb-10">
          <p className="sl-section-tag mb-3">YOUR JOURNEY</p>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-[-0.03em] text-[#1A1A1A] leading-tight">
            Every day counted.
          </h1>
          <p className="mt-3 text-sm md:text-base text-[#5F5F5F]">
            A quiet record of what you kept and what you let slip.
          </p>
        </div>

        {/* ===== PERFORMANCE OVERVIEW ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Performance Ring Card */}
          <div className={cn(
            "lg:col-span-1 p-6 backdrop-blur-xl rounded-2xl",
            isDark ? "bg-zinc-900/50 border border-white/[0.06]" : "bg-white shadow-lg border border-[#EEEAE0]"
          )}>
            <div className="flex flex-col items-center">
              <PerformanceRing completed={totalCompleted} failed={totalFailed} size={140} isDark={isDark} />
              
              <div className="mt-6 w-full grid grid-cols-2 gap-3">
                <div className={cn(
                  "text-center p-3 rounded-xl border",
                  isDark ? "bg-emerald-500/10 border-emerald-500/20" : "bg-emerald-50 border-[#EEEAE0]"
                )}>
                  <p className={cn("text-2xl font-bold", isDark ? "text-emerald-400" : "text-emerald-600")}>{totalCompleted}</p>
                  <p className={cn("text-xs", isDark ? "text-emerald-500/70" : "text-emerald-600/70")}>Won</p>
                </div>
                <div className={cn(
                  "text-center p-3 rounded-xl border",
                  isDark ? "bg-red-500/10 border-red-500/20" : "bg-red-50 border-red-200"
                )}>
                  <p className={cn("text-2xl font-bold", isDark ? "text-red-400" : "text-red-600")}>{totalFailed}</p>
                  <p className={cn("text-xs", isDark ? "text-red-500/70" : "text-red-600/70")}>Lost</p>
                </div>
              </div>
            </div>
          </div>

          {/* Monk Level & Stats */}
          <div className="lg:col-span-2 space-y-4">
            {/* Monk Level Card */}
            <div className={cn(
              "p-5 backdrop-blur-xl rounded-2xl",
              isDark ? "bg-zinc-900/50 border border-white/[0.06]" : "bg-white shadow-lg border border-[#EEEAE0]"
            )}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full overflow-hidden ring-2 ring-coral/30 shadow-lg shadow-amber-500/20">
                      <img src={MONK_IMAGE_URL} alt="Monk" className="w-full h-full object-cover" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 px-2 py-0.5 bg-coral rounded-full text-[10px] font-bold text-white">
                      Lv.{stats?.level || 1}
                    </div>
                  </div>
                  <div>
                    <h3 className={cn("text-xl font-bold bg-gradient-to-r text-[#1A1A1A]", monkInfo.color)}>
                      {monkInfo.title}
                    </h3>
                    <p className={isDark ? "text-sm text-zinc-500" : "text-sm text-[#9E8778]"}>{stats?.xp?.toLocaleString() || 0} XP earned</p>
                  </div>
                </div>
                
                <button 
                  onClick={() => setShowMonkRanks(!showMonkRanks)}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-colors",
                    isDark ? "bg-zinc-800/50 text-zinc-400 hover:text-white hover:bg-zinc-700/50" : "bg-white text-[#9E8778] hover:text-[#1A1A1A] hover:bg-gray-200"
                  )}
                >
                  <Award className="w-3.5 h-3.5" />
                  All Ranks
                  <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", showMonkRanks && "rotate-180")} />
                </button>
              </div>

              {/* XP Progress */}
              <div>
                <div className="flex justify-between text-xs mb-2">
                  <span className={isDark ? "text-zinc-500" : "text-[#9E8778]"}>Progress to Level {(stats?.level || 1) + 1}</span>
                  <span className="text-coral font-medium">{xpInLevel}/500 XP</span>
                </div>
                <div className={cn("h-2.5 rounded-full overflow-hidden", isDark ? "bg-zinc-800" : "bg-white")}>
                  <div 
                    className="h-full bg-coral rounded-full transition-all duration-700"
                    style={{ width: `${(xpInLevel / 500) * 100}%` }}
                  />
                </div>
                {nextRankInfo && (
                  <p className={cn("mt-2 text-xs", isDark ? "text-zinc-600" : "text-[#9E8778]")}>
                    Next rank: <span className={isDark ? "text-zinc-400" : "text-[#5F5F5F]"}>{nextRankInfo.emoji} {nextRankInfo.title}</span> at Level {nextRankInfo.min}
                  </p>
                )}
              </div>

              {/* Monk Ranks Grid (Collapsible) */}
              {showMonkRanks && (
                <div className={cn("mt-4 pt-4 border-t", isDark ? "border-zinc-800" : "border-[#EEEAE0]")}>
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                    {PANDA_LEVELS.map((level) => {
                      const isUnlocked = (stats?.level || 1) >= level.min;
                      const isCurrent = monkInfo.title === level.title;
                      
                      return (
                        <div 
                          key={level.min}
                          className={cn(
                            "p-2 rounded-lg text-center transition-all",
                            isCurrent 
                              ? "bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-[#F5D5CB]" 
                              : isUnlocked 
                                ? isDark ? "bg-zinc-800/50 border border-zinc-700" : "bg-white border border-[#EEEAE0]"
                                : isDark ? "bg-zinc-900/50 border border-zinc-800 opacity-40" : "bg-white border border-[#EEEAE0] opacity-40"
                          )}
                        >
                          <div className={cn("text-xl mb-0.5", !isUnlocked && "grayscale")}>
                            {level.emoji}
                          </div>
                          <p className={cn(
                            "text-[9px] font-medium truncate",
                            isCurrent ? "text-coral" : isUnlocked ? (isDark ? "text-zinc-300" : "text-[#5F5F5F]") : (isDark ? "text-zinc-600" : "text-[#9E8778]")
                          )}>
                            {level.title}
                          </p>
                          <p className={cn("text-[8px]", isDark ? "text-zinc-600" : "text-[#9E8778]")}>Lv.{level.min}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Quick Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard 
                icon={Target} 
                value={activeGoals.length} 
                label="Active" 
                colorClass="bg-blue-500/15 text-blue-400"
              />
              <StatCard 
                icon={Flame} 
                value={stats?.streak || 0} 
                label="Day Streak" 
                colorClass="bg-coral/15 text-orange-400"
              />
              <StatCard 
                icon={Users} 
                value={sharedGoals.length} 
                label="Shared Goals" 
                colorClass="bg-[#F4F3EE]0/15 text-violet-400"
              />
              <StatCard 
                icon={Zap} 
                value={`$${stats?.total_saved || 0}`} 
                label="Pledges Kept" 
                colorClass="bg-emerald-500/15 text-emerald-400"
              />
            </div>
          </div>
        </div>

        {/* ===== FILTER TABS ===== */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setFilter('all')}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all",
              filter === 'all' 
                ? "bg-[#1A1A1A] text-white shadow-lg" 
                : isDark ? "bg-zinc-800/50 text-zinc-400 hover:text-white hover:bg-zinc-700/50" : "bg-white shadow-sm text-[#5F5F5F] hover:text-[#1A1A1A] hover:shadow-md border border-[#EEEAE0]"
            )}
          >
            <HistoryIcon className="w-4 h-4" />
            All History
            <span className={cn("px-1.5 py-0.5 rounded text-xs", filter === 'all' ? "bg-white/20" : isDark ? "bg-black/20" : "bg-[#F4F3EE]")}>{allPastGoals.length}</span>
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all",
              filter === 'completed' 
                ? "bg-emerald-500 text-white shadow-lg" 
                : isDark ? "bg-zinc-800/50 text-zinc-400 hover:text-white hover:bg-zinc-700/50" : "bg-white shadow-sm text-[#5F5F5F] hover:text-[#1A1A1A] hover:shadow-md border border-[#EEEAE0]"
            )}
          >
            <Trophy className="w-4 h-4" />
            Won
            <span className={cn("px-1.5 py-0.5 rounded text-xs", filter === 'completed' ? "bg-white/20" : isDark ? "bg-black/20" : "bg-emerald-100")}>{completedGoals.length}</span>
          </button>
          <button
            onClick={() => setFilter('failed')}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all",
              filter === 'failed' 
                ? "bg-red-500 text-white shadow-lg" 
                : isDark ? "bg-zinc-800/50 text-zinc-400 hover:text-white hover:bg-zinc-700/50" : "bg-white shadow-sm text-[#5F5F5F] hover:text-[#1A1A1A] hover:shadow-md border border-[#EEEAE0]"
            )}
          >
            <X className="w-4 h-4" />
            Lost
            <span className={cn("px-1.5 py-0.5 rounded text-xs", filter === 'failed' ? "bg-white/20" : isDark ? "bg-black/20" : "bg-red-100")}>{failedGoals.length}</span>
          </button>
          <button
            onClick={() => setFilter('shared')}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all",
              filter === 'shared' 
                ? "bg-[#F4F3EE]0 text-white shadow-lg" 
                : isDark ? "bg-zinc-800/50 text-zinc-400 hover:text-white hover:bg-zinc-700/50" : "bg-white shadow-sm text-[#5F5F5F] hover:text-[#1A1A1A] hover:shadow-md border border-[#EEEAE0]"
            )}
          >
            <Users className="w-4 h-4" />
            Shared
            <span className={cn("px-1.5 py-0.5 rounded text-xs", filter === 'shared' ? "bg-white/20" : isDark ? "bg-black/20" : "bg-[#F4F3EE]")}>{sharedGoals.length}</span>
          </button>
        </div>

        {/* ===== GOALS TIMELINE ===== */}
        <div className="space-y-6">
          {filter === 'shared' ? (
            // Shared Goals View
            <>
              {sharedGoals.length === 0 ? (
                <div className={cn(
                  "text-center py-16 rounded-2xl border",
                  isDark ? "bg-zinc-900/30 border-white/5" : "bg-white shadow-lg border-[#EEEAE0]"
                )}>
                  <Users className={cn("w-12 h-12 mx-auto mb-4", isDark ? "text-zinc-700" : "text-slate-300")} />
                  <h3 className={cn("text-lg font-medium mb-2", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>No Shared Goals Yet</h3>
                  <p className={cn("text-sm mb-6", isDark ? "text-zinc-600" : "text-[#9E8778]")}>Join or create a shared goal to compete with friends!</p>
                  <Button onClick={() => navigate('/shared-goals')} className="btn-glossy">
                    Browse Shared Goals
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {sharedGoals.map(goal => (
                    <GoalHistoryCard key={goal.id} goal={goal} type="shared" />
                  ))}
                </div>
              )}
            </>
          ) : (
            // Individual Goals View
            <>
              {filteredGoals.length === 0 ? (
                <div className="text-center py-16 bg-zinc-900/30 rounded-2xl border border-white/5">
                  <Trophy className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-zinc-400 mb-2">
                    {filter === 'all' ? 'No Past Goals Yet' : 
                     filter === 'completed' ? 'No Completed Goals Yet' :
                     'No Failed Goals Yet'}
                  </h3>
                  <p className="text-sm text-zinc-600 mb-6">
                    {filter === 'all' ? 'Complete your first goal to start building your history!' :
                     filter === 'completed' ? 'Keep pushing! Your first win is coming!' :
                     'Great job! Keep up the winning streak!'}
                  </p>
                  <Button onClick={() => navigate('/create-goal')} className="btn-glossy">
                    Create a Goal
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredGoals.map(goal => (
                    <GoalHistoryCard key={goal.id} goal={goal} type="individual" />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* ===== XP EARNING GUIDE ===== */}
        <div className={cn(
          "mt-10 p-6 backdrop-blur-xl border rounded-2xl",
          isDark ? "bg-zinc-900/30 border-white/5" : "bg-white shadow-sm border-[#EEEAE0]"
        )}>
          <div className="flex items-center gap-3 mb-4">
            <div className={cn("p-2 rounded-lg", isDark ? "bg-[#FDF0EC]0/15" : "bg-[#FDF0EC]")}>
              <Star className={cn("w-5 h-5", isDark ? "text-coral" : "text-coral")} />
            </div>
            <h3 className={cn("text-base font-semibold", isDark ? "text-white" : "text-[#1A1A1A]")}>How to Earn XP</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={cn(
              "p-3 rounded-xl border",
              isDark ? "bg-zinc-800/30 border-white/5" : "bg-[#FDF0EC]/50 border-amber-100"
            )}>
              <p className={cn("font-bold text-lg mb-1", isDark ? "text-coral" : "text-coral")}>+100 XP</p>
              <p className={cn("text-xs", isDark ? "text-zinc-500" : "text-[#5F5F5F]")}>Complete a goal</p>
            </div>
            <div className={cn(
              "p-3 rounded-xl border",
              isDark ? "bg-zinc-800/30 border-white/5" : "bg-[#FDF0EC]/50 border-amber-100"
            )}>
              <p className={cn("font-bold text-lg mb-1", isDark ? "text-coral" : "text-coral")}>+10 XP</p>
              <p className={cn("text-xs", isDark ? "text-zinc-500" : "text-[#5F5F5F]")}>Daily log entry</p>
            </div>
            <div className={cn(
              "p-3 rounded-xl border",
              isDark ? "bg-zinc-800/30 border-white/5" : "bg-[#FDF0EC]/50 border-amber-100"
            )}>
              <p className={cn("font-bold text-lg mb-1", isDark ? "text-coral" : "text-coral")}>+50 XP</p>
              <p className={cn("text-xs", isDark ? "text-zinc-500" : "text-[#5F5F5F]")}>7-day streak</p>
            </div>
            <div className={cn(
              "p-3 rounded-xl border",
              isDark ? "bg-zinc-800/30 border-white/5" : "bg-[#FDF0EC]/50 border-amber-100"
            )}>
              <p className={cn("font-bold text-lg mb-1", isDark ? "text-coral" : "text-coral")}>+200 XP</p>
              <p className={cn("text-xs", isDark ? "text-zinc-500" : "text-[#5F5F5F]")}>30-day streak</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
